/**
 * Create IPO Order — an admin raising an IPO application on an investor's behalf.
 *
 * The counterpart of Create RFQ Order, for the primary market. It follows the
 * same shape as that screen (investor picker, instrument picker, derived
 * figures, one submit) and posts the same `/ipo/ipo_ordercreate/` payload the
 * user platform's own IPO journey posts.
 *
 * WHAT THIS DOES NOT DO. Creating the application is not funding it: the
 * investor still has to approve the UPI mandate their bank raises against the
 * id entered here. That is the same on the user platform; there is no admin
 * path that can approve a mandate for someone.
 *
 * The exchange answers 200 even when it rejects a bid, so the verdict is read
 * out of the body rather than the status — see `readOrderOutcome`.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RouteComponentProps, withRouter } from "react-router-dom";
import toastr from "toastr";
import Listbox from "../Listbox";
import { StyledTextInput } from "../StyledComponents/StyledInputs";
import { sxRoot, sxText, sxTypo, sxBox } from "../../Helpers/helpers";
import { makeGETRequestOnUserService } from "../../Api/api";
import { IUser } from "../YieldCalculator/Interfaces";
import {
  createIPOOrder,
  fetchIPODetails,
  fetchIPOSecurityMasters,
  fetchInvestorDetails,
  getIPOErrorMessage,
} from "./ipoOrderApi";
import {
  buildIPOOrderPayload,
  compareSeries,
  getDefaultDemat,
  getSeriesFaceValue,
  getIssuerName,
  getSeriesUnitBounds,
  isCategory4Disabled,
  isIpoOpenForApplication,
  parseSeriesNumber,
  readOrderOutcome,
  splitDematId,
  validateIPOOrder,
} from "./helpers";
import {
  DEPOSITORY_OPTIONS,
  HNI_CATEGORY,
  RETAIL_CATEGORY,
  RETAIL_MAX_INVESTMENT,
} from "./constants";
import type {
  IPODetailsQuote,
  IPODetailsResponse,
  IPOInvestorDetails,
  IPOOrderFormState,
  IPOSecurityMasterListRow,
} from "./interfaces";

interface Option {
  id: string;
  name: string;
}

/** Tab value for series whose category the API did not publish as 3 or 4. */
const OTHER_CATEGORY_TAB = -1;

const EMPTY_FORM: IPOOrderFormState = {
  applicantName: "",
  panNumber: "",
  depository: "NSDL",
  dpId6: "",
  dpId8: "",
  clientId: "",
  upiId: "",
  units: "",
};

const rupees = (value: number) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const categoryLabel = (category?: string | number | null) => {
  const value = parseSeriesNumber(category);
  if (value === RETAIL_CATEGORY) return "Retail";
  if (value === HNI_CATEGORY) return "HNI";
  return String(category ?? "—");
};

const CreateIPOOrder: React.FC<RouteComponentProps> = (props) => {
  /* ── investor ── */
  const [userOpen, setUserOpen] = useState(false);
  const [users, setUsers] = useState<readonly Option[]>([]);
  const [user, setUser] = useState<Option | null>(null);
  const [investor, setInvestor] = useState<IPOInvestorDetails | null>(null);
  const [investorLoading, setInvestorLoading] = useState(false);
  const loadingUsers = userOpen && users.length === 0;

  /* ── issue ── */
  const [ipoOpen, setIpoOpen] = useState(false);
  const [ipos, setIpos] = useState<readonly Option[]>([]);
  const [ipo, setIpo] = useState<Option | null>(null);
  const [closedCount, setClosedCount] = useState(0);
  const [details, setDetails] = useState<IPODetailsResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  /*
   * Tracked explicitly rather than inferred from `ipos.length === 0`, the way
   * the RFQ screen does it. Closed issues are filtered out, so "no options" is
   * a normal resting state — inferring loading from an empty list would refetch
   * on every open and never let the empty message show.
   *
   * The "already asked" latch is a ref, not state, and the effect depends only
   * on `ipoOpen`. Putting the loading flag in the dependency list made the
   * effect re-run the moment it set that flag, and its own cleanup then marked
   * the in-flight request stale — so a perfectly good response was thrown away
   * and the field said "Loading…" for ever.
   */
  const [iposLoading, setIposLoading] = useState(false);
  const [iposLoaded, setIposLoaded] = useState(false);
  const iposRequested = useRef(false);
  const mounted = useRef(true);

  /* ── application ── */
  const [quoteUuid, setQuoteUuid] = useState("");
  const [categoryTab, setCategoryTab] = useState<number>(RETAIL_CATEGORY);
  const [switchNote, setSwitchNote] = useState("");
  const [form, setForm] = useState<IPOOrderFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const [result, setResult] = useState<{
    accepted: boolean;
    message: string;
    bidId: string;
    applicationNo: string;
  } | null>(null);

  const patch = (changes: Partial<IPOOrderFormState>) => {
    setForm((current) => ({ ...current, ...changes }));
    setResult(null);
  };

  /* ── investor list ── */
  useEffect(() => {
    let active = true;
    if (!loadingUsers) return undefined;
    makeGETRequestOnUserService("/user/user_list/")
      .then((response: any) => {
        if (!active) return;
        const rows: IUser[] = Array.isArray(response?.data) ? response.data : [];
        setUsers(
          rows
            .map((row) => ({
              id: row.uuid,
              name: `${[row.firstName, row.lastName].filter(Boolean).join(" ") || "SMEST user"}${
                row.smestID ? ` · ${row.smestID}` : ""
              }${row.emailId ? ` · ${row.emailId}` : ""}`,
            }))
            .sort((left, right) => left.name.localeCompare(right.name))
        );
      })
      .catch((error: any) => toastr.error(getIPOErrorMessage(error, "Unable to load users.")));
    return () => {
      active = false;
    };
  }, [loadingUsers]);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  /* ── issue list ── */
  useEffect(() => {
    // Closing the dropdown must not abandon a request that is already out —
    // only unmounting does, which `mounted` tracks.
    if (!ipoOpen || iposRequested.current) return;
    iposRequested.current = true;
    setIposLoading(true);
    fetchIPOSecurityMasters()
      .then((rows: IPOSecurityMasterListRow[]) => {
        if (!mounted.current) return;
        const masters = rows.map((row) => row.ipo_security_master).filter(Boolean);
        // Only issues still open to applications, by the same rule the user
        // platform lists them on Explore Bonds — see isIpoOpenForApplication.
        const open = masters.filter((master) =>
          isIpoOpenForApplication(master.issue_close_date)
        );
        setClosedCount(masters.length - open.length);
        setIpos(
          open
            .map((master) => ({
              id: master.uuid,
              name: `${
                getIssuerName(master) || master.issue_description || master.scrip_id
              } · ${master.scrip_id}`,
            }))
            .sort((left, right) => left.name.localeCompare(right.name))
        );
        setIposLoaded(true);
      })
      .catch((error: any) => {
        // Let the next open try again rather than latching a failed load.
        iposRequested.current = false;
        if (mounted.current) {
          toastr.error(getIPOErrorMessage(error, "Unable to load the IPO list."));
        }
      })
      .then(() => {
        if (mounted.current) setIposLoading(false);
      });
  }, [ipoOpen]);

  /* ── investor profile, and the prefill it drives ── */
  useEffect(() => {
    if (!user) {
      setInvestor(null);
      setForm((current) => ({ ...EMPTY_FORM, units: current.units }));
      return;
    }
    let active = true;
    setInvestorLoading(true);
    fetchInvestorDetails(user.id)
      .then((profile) => {
        if (!active) return;
        setInvestor(profile);
        const demat = getDefaultDemat(profile);
        const split = splitDematId(demat?.dpType, demat?.id);
        setForm((current) => ({
          ...current,
          applicantName:
            profile.panCard?.name ||
            [profile.firstName, profile.lastName].filter(Boolean).join(" "),
          panNumber: profile.panCard?.panCardNumber || "",
          depository: split.depository || "NSDL",
          dpId6: split.dpId6,
          dpId8: split.dpId8,
          clientId: split.clientId,
        }));
      })
      .catch((error: any) => {
        if (!active) return;
        setInvestor(null);
        toastr.error(getIPOErrorMessage(error, "Unable to load this investor."));
      })
      .then(() => {
        if (active) setInvestorLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  /* ── issue details ── */
  useEffect(() => {
    if (!ipo) {
      setDetails(null);
      setQuoteUuid("");
      return;
    }
    let active = true;
    setDetailsLoading(true);
    setQuoteUuid("");
    fetchIPODetails(ipo.id)
      .then((response) => {
        if (!active) return;
        setDetails(response);
      })
      .catch((error: any) => {
        if (!active) return;
        setDetails(null);
        toastr.error(getIPOErrorMessage(error, "Unable to load this IPO."));
      })
      .then(() => {
        if (active) setDetailsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [ipo]);

  const securityMaster = details ? details.ipo_security_master : null;
  const quotes = useMemo(
    () => (details ? [...(details.ipo_quotes || [])].sort(compareSeries) : []),
    [details]
  );
  const quote: IPODetailsQuote | null =
    quotes.find((row) => row.uuid === quoteUuid) || null;

  /*
   * Series are grouped by the 3/4 bucket rather than listed flat.
   *
   * An issue publishes the same series twice — once for Retail, once for HNI —
   * so a flat list showed "Series 1" two or three times with nothing to
   * distinguish them, and the admin only discovered they had picked the wrong
   * one after typing a quantity and hitting a validation error. Only one
   * bucket can ever be right for a given amount, so the amount is captured
   * first and the bucket it implies is the one shown.
   */
  const categorised = useMemo(() => {
    const retail: IPODetailsQuote[] = [];
    const hni: IPODetailsQuote[] = [];
    const other: IPODetailsQuote[] = [];
    quotes.forEach((row) => {
      const category = parseSeriesNumber(row.category);
      if (category === RETAIL_CATEGORY) retail.push(row);
      else if (category === HNI_CATEGORY) hni.push(row);
      else other.push(row);
    });
    return { retail, hni, other };
  }, [quotes]);

  const categoryTabs = useMemo(() => {
    const tabs: Array<{
      value: number;
      label: string;
      band: string;
      rows: IPODetailsQuote[];
    }> = [];
    if (categorised.retail.length) {
      tabs.push({
        value: RETAIL_CATEGORY,
        label: `Retail · category ${RETAIL_CATEGORY}`,
        band: `up to ${rupees(RETAIL_MAX_INVESTMENT)}`,
        rows: categorised.retail,
      });
    }
    if (categorised.hni.length) {
      tabs.push({
        value: HNI_CATEGORY,
        label: `HNI · category ${HNI_CATEGORY}`,
        band: `above ${rupees(RETAIL_MAX_INVESTMENT)}`,
        rows: categorised.hni,
      });
    }
    // Anything whose category cannot be read still has to be reachable, or a
    // data change upstream would silently hide every series.
    if (categorised.other.length) {
      tabs.push({
        value: OTHER_CATEGORY_TAB,
        label: "Other",
        band: "category not published",
        rows: categorised.other,
      });
    }
    return tabs;
  }, [categorised]);

  const activeTab =
    categoryTabs.find((tab) => tab.value === categoryTab) || categoryTabs[0] || null;

  /* A new issue starts on the bucket it can actually take. */
  useEffect(() => {
    if (!securityMaster) return;
    setCategoryTab(
      isCategory4Disabled(securityMaster.uuid) ? HNI_CATEGORY : RETAIL_CATEGORY
    );
    setSwitchNote("");
  }, [securityMaster]);

  const validation = useMemo(
    () =>
      validateIPOOrder({
        investorUuid: investor ? investor.uuid : "",
        securityMaster,
        quote,
        form,
      }),
    [investor, securityMaster, quote, form]
  );

  /*
   * Crossing ₹2,00,000 moves the application between buckets, so the selection
   * follows it rather than sitting there invalid.
   *
   * The user platform does the same (`promoteRetailToHni` / `demoteHniToRetail`
   * in SelectSeries.tsx): it keeps the series number and swaps the category.
   * When the issue publishes no counterpart for that series the selection is
   * cleared instead, because there is nothing correct to move it to.
   */
  useEffect(() => {
    const derived = validation.derivedCategory;
    if (!derived || !quote || !securityMaster) return;
    if (isCategory4Disabled(securityMaster.uuid)) return;
    const current = parseSeriesNumber(quote.category);
    if (current == null || current === derived) return;

    const counterpart = quotes.find(
      (row) =>
        parseSeriesNumber(row.category) === derived &&
        row.series_frontend === quote.series_frontend
    );
    setCategoryTab(derived);
    const amount = rupees(validation.totalInvestment);
    const side = derived === HNI_CATEGORY ? "above" : "at or below";
    if (counterpart) {
      setQuoteUuid(counterpart.uuid);
      setSwitchNote(
        `${amount} is ${side} ${rupees(RETAIL_MAX_INVESTMENT)}, so this moved to the ${categoryLabel(derived)} version of series ${quote.series_frontend}.`
      );
    } else {
      setQuoteUuid("");
      setSwitchNote(
        `${amount} is ${side} ${rupees(RETAIL_MAX_INVESTMENT)}, and this issue publishes no ${categoryLabel(derived)} version of series ${quote.series_frontend}. Pick another series or change the quantity.`
      );
    }
  }, [
    validation.derivedCategory,
    validation.totalInvestment,
    quote,
    quotes,
    securityMaster,
  ]);

  const kycVerified = investor ? investor.areKYCDetailsVerified === true : false;
  const canSubmit =
    !validation.problems.length && !submitting && !investorLoading && !detailsLoading;

  const submit = async () => {
    setShowProblems(true);
    setResult(null);
    if (!canSubmit || !investor || !securityMaster || !quote) return;

    setSubmitting(true);
    try {
      const payload = buildIPOOrderPayload({
        investor,
        securityMaster,
        quote,
        form,
        units: validation.units,
        faceValue: validation.faceValue,
        totalInvestment: validation.totalInvestment,
      });
      const response = await createIPOOrder(payload);
      const outcome = readOrderOutcome(response);
      setResult(outcome);
      if (outcome.accepted) {
        toastr.success("IPO application created.");
      } else {
        toastr.error(outcome.message);
      }
    } catch (error) {
      toastr.error(getIPOErrorMessage(error, "The IPO order could not be created."));
    } finally {
      setSubmitting(false);
    }
  };

  const showProblemList = showProblems && validation.problems.length > 0;

  return (
    <Box
      sx={{
        maxWidth: ["95%", "80%"],
        mx: "auto",
        my: 5,
        bgcolor: "#FFFFFF",
        p: 3,
        borderRadius: 5,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Create IPO Order
      </Typography>
      <Typography sx={{ color: "#6b7280", fontSize: 13, mb: 3 }}>
        Raises an IPO application on an investor&apos;s behalf. Creating it does
        not fund it — the investor still has to approve the UPI mandate their
        bank raises against the ID below.
      </Typography>

      <Grid container spacing={3}>
        {/* ── who ── */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            open={userOpen}
            onOpen={() => setUserOpen(true)}
            onClose={() => setUserOpen(false)}
            value={user}
            onChange={(_, value) => {
              setUser(value);
              setResult(null);
            }}
            options={users}
            loading={loadingUsers}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            size="medium"
            sx={sxRoot}
            popupIcon={<ExpandMoreIcon />}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Investor"
                sx={(theme) => sxText(theme)}
                fullWidth
                margin="dense"
                variant="standard"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(optionProps, option, { selected }) => (
              <Typography {...optionProps} sx={sxTypo(selected)} variant="h6">
                {option.name}
              </Typography>
            )}
            PaperComponent={({ children }) => <Box sx={sxBox}>{children}</Box>}
            ListboxComponent={Listbox}
          />
          {investorLoading && (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 1 }}>
              <CircularProgress size={12} sx={{ mr: 1 }} /> Loading the investor&apos;s profile…
            </Typography>
          )}
          {investor && !investorLoading && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={kycVerified ? "KYC verified" : "KYC not verified"}
                color={kycVerified ? "success" : "warning"}
                variant="outlined"
              />
              {investor.accountType && (
                <Chip size="small" label={investor.accountType} variant="outlined" />
              )}
              {!investor.panCard?.panCardNumber && (
                <Chip size="small" label="No PAN on file" color="warning" variant="outlined" />
              )}
              {!getDefaultDemat(investor) && (
                <Chip size="small" label="No demat on file" color="warning" variant="outlined" />
              )}
            </Box>
          )}
        </Grid>

        {/* ── which issue ── */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            open={ipoOpen}
            onOpen={() => setIpoOpen(true)}
            onClose={() => setIpoOpen(false)}
            value={ipo}
            onChange={(_, value) => {
              setIpo(value);
              setResult(null);
            }}
            options={ipos}
            loading={iposLoading}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            size="medium"
            sx={sxRoot}
            popupIcon={<ExpandMoreIcon />}
            renderInput={(params) => (
              <TextField
                {...params}
                label="IPO"
                sx={(theme) => sxText(theme)}
                fullWidth
                margin="dense"
                variant="standard"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {iposLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(optionProps, option, { selected }) => (
              <Typography {...optionProps} sx={sxTypo(selected)} variant="h6">
                {option.name}
              </Typography>
            )}
            PaperComponent={({ children }) => <Box sx={sxBox}>{children}</Box>}
            ListboxComponent={Listbox}
          />
          {iposLoaded && !iposLoading && !ipos.length && (
            <Typography sx={{ fontSize: 12, color: "#b45309", mt: 1 }}>
              No IPO is open for applications right now
              {closedCount ? ` — ${closedCount} have closed` : ""}.
            </Typography>
          )}
          {iposLoaded && !iposLoading && ipos.length > 0 && closedCount > 0 && (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 1 }}>
              Showing {ipos.length} open{" "}
              {ipos.length === 1 ? "issue" : "issues"}; {closedCount} closed{" "}
              {closedCount === 1 ? "issue is" : "issues are"} hidden.
            </Typography>
          )}
          {detailsLoading && (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 1 }}>
              <CircularProgress size={12} sx={{ mr: 1 }} /> Loading the issue and its series…
            </Typography>
          )}
          {securityMaster && !detailsLoading && (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 1 }}>
              Scrip {securityMaster.scrip_id} · face value{" "}
              {rupees(securityMaster.face_value)} · open{" "}
              {formatDate(securityMaster.issue_open_date)} – closes{" "}
              {formatDate(securityMaster.issue_close_date)}
            </Typography>
          )}
        </Grid>
      </Grid>

      {securityMaster && isCategory4Disabled(securityMaster.uuid) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Retail (category {RETAIL_CATEGORY}) is closed on this issue — every
          application has to be sized as HNI.
        </Alert>
      )}

      {/* ── how much, then which series ── */}
      {securityMaster && !detailsLoading && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Application size
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 2 }}>
            The amount decides the category, and the category decides which
            series are on offer — so it is entered first.
          </Typography>

          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel shrink htmlFor="ipo-units">
                  Number of bonds
                </InputLabel>
                <StyledTextInput
                  id="ipo-units"
                  type="number"
                  value={form.units}
                  inputProps={{
                    min: validation.bounds.min,
                    max: validation.bounds.max,
                  }}
                  onChange={(event: any) => patch({ units: event.target.value })}
                />
                <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.5 }}>
                  {validation.bounds.min}–{validation.bounds.max} bonds
                  {validation.faceValue
                    ? ` · ${rupees(validation.faceValue)} each`
                    : ""}
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={4} md={3}>
              <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                Amount
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                {validation.totalInvestment
                  ? rupees(validation.totalInvestment)
                  : "—"}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={4} md={6}>
              <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                Which category that is
              </Typography>
              {validation.derivedCategory ? (
                <Chip
                  size="small"
                  color="primary"
                  sx={{ mt: 0.5 }}
                  label={`${categoryLabel(validation.derivedCategory)} · category ${validation.derivedCategory}`}
                />
              ) : (
                <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 0.5 }}>
                  Enter a quantity
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.5 }}>
                Above {rupees(RETAIL_MAX_INVESTMENT)} is HNI (category{" "}
                {HNI_CATEGORY}); at or below it is Retail (category{" "}
                {RETAIL_CATEGORY}).
              </Typography>
            </Grid>
          </Grid>

          {switchNote && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              onClose={() => setSwitchNote("")}
            >
              {switchNote}
            </Alert>
          )}

          <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Series</Typography>

          {!quotes.length ? (
            <Alert severity="warning">
              This issue has no series on offer, so no application can be raised
              against it.
            </Alert>
          ) : !activeTab ? null : (
            <>
              {categoryTabs.length > 1 && (
                <Tabs
                  value={activeTab.value}
                  onChange={(_, value) => {
                    setCategoryTab(value);
                    setSwitchNote("");
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: "1px solid #e5e7eb", mb: 2 }}
                >
                  {categoryTabs.map((tab) => {
                    /* The bucket the current amount falls in is the only one
                       that can be submitted, so it is marked rather than left
                       for the admin to work out. */
                    const matches =
                      validation.derivedCategory != null &&
                      tab.value === validation.derivedCategory;
                    return (
                      <Tab
                        key={tab.value}
                        value={tab.value}
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                        label={
                          <Box sx={{ textAlign: "left" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                              {tab.label}
                              {matches ? " ✓" : ""}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                              {tab.band} · {tab.rows.length} series
                              {matches ? " · matches this amount" : ""}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </Tabs>
              )}

              {validation.derivedCategory != null &&
                activeTab.value !== validation.derivedCategory &&
                activeTab.value !== OTHER_CATEGORY_TAB && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {rupees(validation.totalInvestment)} is a{" "}
                    {categoryLabel(validation.derivedCategory)} application.
                    Series on this tab cannot be used for it — switch to{" "}
                    {categoryLabel(validation.derivedCategory)}, or change the
                    quantity.
                  </Alert>
                )}

              <Grid container spacing={2}>
                {activeTab.rows.map((row) => {
                  const selected = row.uuid === quoteUuid;
                  const seriesFaceValue = getSeriesFaceValue(
                    row.series_frontend,
                    Number(securityMaster.face_value) || 0,
                    securityMaster.uuid
                  );
                  const bounds = getSeriesUnitBounds(
                    row.series_frontend,
                    securityMaster.uuid
                  );
                  /* With the quantity already known, each card can state what
                     this application would actually cost on that series. */
                  const cardAmount =
                    validation.units > 0
                      ? validation.units * seriesFaceValue
                      : 0;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={row.uuid}>
                      <Box
                        onClick={() => {
                          setQuoteUuid(row.uuid);
                          setSwitchNote("");
                          setResult(null);
                        }}
                        sx={{
                          cursor: "pointer",
                          border: selected
                            ? "2px solid #2563eb"
                            : "1px solid #e5e7eb",
                          bgcolor: selected ? "#eff6ff" : "#fff",
                          borderRadius: 3,
                          p: 2,
                          height: "100%",
                          "&:hover": { borderColor: "#93c5fd" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            Series {row.series_frontend}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            {row.quote_yield}%
                          </Typography>
                        </Box>
                        <Typography
                          sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}
                        >
                          {row.payment_term || "—"} · {row.tenure || "—"}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: "#6b7280", mt: 1 }}
                        >
                          {rupees(seriesFaceValue)} per bond · {bounds.min}–
                          {bounds.max} bonds
                        </Typography>
                        {cardAmount > 0 && (
                          <Typography sx={{ fontSize: 12, fontWeight: 700, mt: 1 }}>
                            {validation.units} bonds = {rupees(cardAmount)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </>
      )}

      {/* ── the investor’s own details ── */}
      {quote && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Applicant details
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 2 }}>
            Prefilled from the investor’s KYC record. Correct anything the
            exchange should see differently.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel shrink htmlFor="ipo-applicant">
                  Applicant name
                </InputLabel>
                <StyledTextInput
                  id="ipo-applicant"
                  value={form.applicantName}
                  onChange={(event: any) =>
                    patch({ applicantName: event.target.value })
                  }
                />
                <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.5 }}>
                  Must match the PAN record.
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel shrink htmlFor="ipo-pan">
                  PAN
                </InputLabel>
                <StyledTextInput
                  id="ipo-pan"
                  value={form.panNumber}
                  inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
                  onChange={(event: any) =>
                    patch({ panNumber: event.target.value.toUpperCase() })
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="standard">
                <InputLabel shrink htmlFor="ipo-depository">
                  Depository
                </InputLabel>
                <Select
                  id="ipo-depository"
                  variant="standard"
                  value={form.depository}
                  onChange={(event: any) =>
                    patch({ depository: event.target.value })
                  }
                  sx={{ mt: 2 }}
                >
                  {DEPOSITORY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {form.depository === "NSDL" ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel shrink htmlFor="ipo-dpid">
                      DP ID (after IN)
                    </InputLabel>
                    <StyledTextInput
                      id="ipo-dpid"
                      value={form.dpId6}
                      inputProps={{ maxLength: 6, inputMode: "numeric" }}
                      onChange={(event: any) =>
                        patch({ dpId6: event.target.value.replace(/\D/g, "").slice(0, 6) })
                      }
                    />
                    <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.5 }}>
                      Sent as IN{form.dpId6 || "······"}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel shrink htmlFor="ipo-benfid">
                      Beneficiary ID
                    </InputLabel>
                    <StyledTextInput
                      id="ipo-benfid"
                      value={form.dpId8}
                      inputProps={{ maxLength: 8, inputMode: "numeric" }}
                      onChange={(event: any) =>
                        patch({ dpId8: event.target.value.replace(/\D/g, "").slice(0, 8) })
                      }
                    />
                  </FormControl>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth variant="standard">
                  <InputLabel shrink htmlFor="ipo-clientid">
                    CDSL client ID
                  </InputLabel>
                  <StyledTextInput
                    id="ipo-clientid"
                    value={form.clientId}
                    inputProps={{ maxLength: 16, inputMode: "numeric" }}
                    onChange={(event: any) =>
                      patch({ clientId: event.target.value.replace(/\D/g, "").slice(0, 16) })
                    }
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth variant="standard">
                <InputLabel shrink htmlFor="ipo-upi">
                  UPI ID
                </InputLabel>
                <StyledTextInput
                  id="ipo-upi"
                  value={form.upiId}
                  onChange={(event: any) => patch({ upiId: event.target.value })}
                />
                <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.5 }}>
                  The investor&apos;s own UPI ID — the sponsor bank raises the
                  mandate against it, and only listed handles are accepted.
                </Typography>
              </FormControl>
            </Grid>
          </Grid>

          {/* ── what would be sent ── */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 3,
              bgcolor: "#f8fafc",
              border: "1px solid #e5e7eb",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Quantity</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {validation.units > 0 ? validation.units : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Amount</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {validation.totalInvestment ? rupees(validation.totalInvestment) : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                  Category by amount
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {validation.derivedCategory
                    ? `${categoryLabel(validation.derivedCategory)} (${validation.derivedCategory})`
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                  Series category code
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {quote.category_subcode || "—"}
                </Typography>
              </Grid>
            </Grid>
            <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 1.5 }}>
              Applications above {rupees(RETAIL_MAX_INVESTMENT)} are HNI
              (category {HNI_CATEGORY}); at or below it they are Retail (category{" "}
              {RETAIL_CATEGORY}). The series has to match.
            </Typography>
          </Box>
        </>
      )}

      {showProblemList && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <AlertTitle>This application cannot be sent yet</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {validation.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Alert>
      )}

      {investor && !kycVerified && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This investor&apos;s KYC is not verified. The exchange will reject an
          application against an unverified PAN.
        </Alert>
      )}

      {result && (
        <Alert severity={result.accepted ? "success" : "error"} sx={{ mt: 3 }}>
          <AlertTitle>
            {result.accepted ? "Application created" : "The exchange rejected this bid"}
          </AlertTitle>
          {result.message}
          {result.accepted && (
            <Typography sx={{ fontSize: 12, mt: 1 }}>
              {result.applicationNo ? `Application ${result.applicationNo}. ` : ""}
              {result.bidId ? `Bid ${result.bidId}. ` : ""}
              The investor still has to approve the UPI mandate before it is funded.
            </Typography>
          )}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
        <Button
          variant="outlined"
          disabled={submitting}
          onClick={() => props.history.push("/ipo_orders")}
        >
          {result && result.accepted ? "Go to IPO Orders" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || (result ? result.accepted : false)}
        >
          {submitting ? (
            <>
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              Creating…
            </>
          ) : (
            "Create IPO order"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default withRouter(CreateIPOOrder);
